import React from "react";

import PdfHeading from "./PdfHeading";
import PdfRichText from "./PdfRichText";

import type {
  PdfBehandlingTiltak, PdfDosering, PdfKontraindikasjon, PdfLocalbehandling, PdfProductTreeNode
} from "./types.d";

// Cover title is h1 and the TOC title is h2, so top-level chapters continue at h2.
const ROOT_NODE_HEADING_LEVEL = 2;

const TEXT_BLOCK_STYLE: React.CSSProperties = { fontSize: "13px", lineHeight: 1.4 };

function capitalize(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

function TextBlock({ html, parentHeadingLevel }: { html?: string | null; parentHeadingLevel: number }) {
  return <PdfRichText html={html} parentHeadingLevel={parentHeadingLevel} style={TEXT_BLOCK_STYLE} />;
}

function LovOrTakst({ item, headingLevel }: { item: PdfProductTreeNode; headingLevel: number }) {
  const subHeadingLevel = headingLevel + 1;

  return (
    <div>
      {item.lovtekst && (
        <>
          <PdfHeading level={subHeadingLevel}>{item.tittel || item.title || item.description}</PdfHeading>
          <TextBlock html={item.lovtekst} parentHeadingLevel={subHeadingLevel} />
        </>
      )}

      {Boolean(item.takstkodes?.length) && item.takstkodes?.map((takst) => {
        const takstKey = `${takst.takstkode ?? "takst"}-${takst.description ?? "beskrivelse"}`;

        return (
          <blockquote key={takstKey} style={{ margin: "12px 0", padding: "8px 12px" }}>
            <div>
              <span>Takst</span>
              <span>
                {` ${takst.takstkode}`}
              </span>
              <span>
                {`: ${takst.description}`}
              </span>
            </div>
            {takst.invalid && (
              <div>
                <span>Ugyldig takstkombinasjon:</span>
                <span>
                  {` ${takst.invalid}`}
                </span>
              </div>
            )}
            {takst.merknader?.map((merk) => {
              const merknadKey = `${merk.merknad_nr ?? "merknad"}-${merk.beskrivelse ?? "beskrivelse"}`;

              return (
                <div key={merknadKey}>
                  {merk.merknad_nr && merk.beskrivelse && (
                    <div>
                      <span>Merknad</span>
                      <span>
                        {` ${merk.merknad_nr}`}
                      </span>
                      <span>
                        {`: ${merk.beskrivelse}`}
                      </span>
                    </div>
                  )}
                </div>
              );
            })}
          </blockquote>
        );
      })}

      {item.text && (
        <>
          <PdfHeading level={subHeadingLevel}>Helsedirektoratets kommentarer</PdfHeading>
          <TextBlock html={item.text} parentHeadingLevel={subHeadingLevel} />
        </>
      )}
    </div>
  );
}

function DoseringList({ list }: { list?: PdfDosering[] }) {
  if (!list?.length) return null;

  return (
    <div>
      {list.map((dosering, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <React.Fragment key={`${dosering.virkestoff ?? "dosering"}-${index}`}>
          {index !== 0 && <span>{"+ "}</span>}
          {dosering.loadingDoseFrequency && (
            <div>{`${dosering.termLadningsdose} ladningsdose etterfulgt av`}</div>
          )}
          {dosering.virkestoff && (
            <div>
              <span>{capitalize(dosering.virkestoff)}</span>
              {dosering.viktig && <span style={{ fontStyle: "italic" }}>{` - ${dosering.viktig}`}</span>}
            </div>
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

function KontraindikasjonList({ list, headingLevel }: { list?: PdfKontraindikasjon[]; headingLevel: number }) {
  if (!list?.length) return null;

  return (
    <div style={{ marginTop: "8px" }}>
      <PdfHeading level={headingLevel}>Spesielle hensyn</PdfHeading>
      {list.map((kontraindikasjon, index) => {
        const textParentHeadingLevel = kontraindikasjon.kodeTilstand ? headingLevel + 1 : headingLevel;

        return (
          <div
            // eslint-disable-next-line react/no-array-index-key
            key={`${kontraindikasjon.medicine ?? "kontraindikasjon"}-${index}`}
            style={{ marginBottom: "6px" }}
          >
            {kontraindikasjon.kodeTilstand && (
              <PdfHeading level={headingLevel + 1}>{capitalize(kontraindikasjon.kodeTilstand)}</PdfHeading>
            )}
            {kontraindikasjon.medicine && <em>{capitalize(kontraindikasjon.medicine)}</em>}
            {kontraindikasjon.text && (
              <TextBlock html={kontraindikasjon.text} parentHeadingLevel={textParentHeadingLevel} />
            )}
          </div>
        );
      })}
    </div>
  );
}

function LocalBehandlingBlock({ item, headingLevel }: { item: PdfLocalbehandling; headingLevel: number }) {
  const kontraindikasjonHeadingLevel = item.title ? headingLevel + 1 : headingLevel;

  return (
    <div style={{ marginBottom: "12px" }}>
      {item.title && <PdfHeading level={headingLevel}>{item.title}</PdfHeading>}
      <DoseringList list={item.doseringList} />
      <KontraindikasjonList list={item.kontraindikasjonList} headingLevel={kontraindikasjonHeadingLevel} />
    </div>
  );
}

function BehandlingBlock({ item, headingLevel }: { item: PdfBehandlingTiltak; headingLevel: number }) {
  const { behandling } = item;
  if (!behandling?.standardbehandling) return null;

  const {
    standardbehandling, standardbehandlingList, andrebehandlingList, overgangtiloral
  } = behandling;
  const contentHeadingLevel = item.title ? headingLevel : headingLevel - 1;
  const sectionHeadingLevel = contentHeadingLevel + 1;
  const localBehandlingHeadingLevel = sectionHeadingLevel + 1;

  return (
    <div style={{ marginTop: "16px" }}>
      {item.title && <PdfHeading level={headingLevel}>{item.title}</PdfHeading>}

      {standardbehandling.viktigInformasjon && (
        <TextBlock html={standardbehandling.viktigInformasjon} parentHeadingLevel={contentHeadingLevel} />
      )}

      {standardbehandling.varighet && (
        <p>
          {`${standardbehandling.varighet.ledetekstVarighetBehandlingAntallDogn}: `}
          {standardbehandling.varighet.varighetBehandlingAntallDogn}
          {" døgn"}
        </p>
      )}

      {Boolean(standardbehandlingList?.length) && (
        <>
          <PdfHeading level={sectionHeadingLevel}>{standardbehandling.overskriftBehandlingsregime}</PdfHeading>
          {standardbehandlingList?.map((standard, index) => (
            <LocalBehandlingBlock
              // eslint-disable-next-line react/no-array-index-key
              key={`${standard.title ?? "standard"}-${index}`}
              item={standard}
              headingLevel={localBehandlingHeadingLevel}
            />
          ))}
        </>
      )}

      {Boolean(andrebehandlingList?.length) && (
        <>
          <PdfHeading level={sectionHeadingLevel}>Behandlingsalternativer</PdfHeading>
          {andrebehandlingList?.map((andre, index) => (
            <LocalBehandlingBlock
              // eslint-disable-next-line react/no-array-index-key
              key={`${andre.title ?? "andre"}-${index}`}
              item={andre}
              headingLevel={localBehandlingHeadingLevel}
            />
          ))}
        </>
      )}

      {Boolean(overgangtiloral?.length) && (
        <>
          <PdfHeading level={sectionHeadingLevel}>Overgang til oral behandling</PdfHeading>
          {overgangtiloral?.map((oral, index) => (
            <LocalBehandlingBlock
              // eslint-disable-next-line react/no-array-index-key
              key={`${oral.title ?? "oral"}-${index}`}
              item={oral}
              headingLevel={localBehandlingHeadingLevel}
            />
          ))}
        </>
      )}
    </div>
  );
}

function BehandlingList({ list, headingLevel }: { list?: PdfBehandlingTiltak[]; headingLevel: number }) {
  if (!list?.length) return null;

  return (
    <div>
      {list.map((behandlingTiltak, index) => (
        <BehandlingBlock
          // eslint-disable-next-line react/no-array-index-key
          key={`${behandlingTiltak.title ?? "behandling"}-${index}`}
          item={behandlingTiltak}
          headingLevel={headingLevel}
        />
      ))}
    </div>
  );
}

function RecommendationBlock({ item, headingLevel }: { item: PdfProductTreeNode; headingLevel: number }) {
  const defaultText = item.text ?? item.tekst ?? "";
  const subHeadingLevel = headingLevel + 1;

  return (
    <>
      {defaultText && <TextBlock html={defaultText} parentHeadingLevel={headingLevel} />}

      {Boolean(item.behandling?.length) && <BehandlingList list={item.behandling} headingLevel={subHeadingLevel} />}

      {item.praktisk && (
        <div style={{ marginTop: "16px" }}>
          <PdfHeading level={subHeadingLevel}>{item.type === "generisk-normerende-enhet" || item.type === "regelverk-lov-eller-forskrift" ? "Veiledning" : "Praktisk informasjon"}</PdfHeading>
          <TextBlock html={item.praktisk} parentHeadingLevel={subHeadingLevel} />
        </div>
      )}

      {(item.lovtekst || item.rasjonale || item.fordelerogulemper || item.kvalitetdokumentasjon || item.verdierogpreferanser || item.ressurshensyn) && (
        <div style={{ marginTop: "16px" }}>
          <PdfHeading level={subHeadingLevel}>{item.type === "regelverk-lov-eller-forskrift" ? "Rettslig grunnlag" : "Begrunnelse"}</PdfHeading>
          {item.lovtekst && <TextBlock html={item.lovtekst} parentHeadingLevel={subHeadingLevel} />}
          {item.rasjonale && <TextBlock html={item.rasjonale} parentHeadingLevel={subHeadingLevel} />}
          {item.fordelerogulemper && <TextBlock html={item.fordelerogulemper} parentHeadingLevel={subHeadingLevel} />}
          {item.kvalitetdokumentasjon && <TextBlock html={item.kvalitetdokumentasjon} parentHeadingLevel={subHeadingLevel} />}
          {item.verdierogpreferanser && <TextBlock html={item.verdierogpreferanser} parentHeadingLevel={subHeadingLevel} />}
          {item.ressurshensyn && <TextBlock html={item.ressurshensyn} parentHeadingLevel={subHeadingLevel} />}
        </div>
      )}
    </>
  );
}

function renderItemContent(item: PdfProductTreeNode, headingLevel: number) {
  if (!item.type || item.type === "seksjon") {
    return null;
  }

  if (item.type === "lov-eller-forskriftstekst-med-kommentar" || item.type === "takst-med-merknad") {
    return <LovOrTakst item={item} headingLevel={headingLevel} />;
  }

  if (item.type === "paragraf-med-kommentar") {
    return (
      <>
        <TextBlock html={item.lovtekst ?? item.text ?? item.tekst} parentHeadingLevel={headingLevel} />
        {item.kommentar && (
          <>
            <PdfHeading level={headingLevel + 1}>Kommentarer</PdfHeading>
            <TextBlock html={item.kommentar} parentHeadingLevel={headingLevel + 1} />
          </>
        )}
      </>
    );
  }

  return <RecommendationBlock item={item} headingLevel={headingLevel} />;
}

type PdfProductTreeNodeRendererProps = {
  item: PdfProductTreeNode;
  depth: number;
  showTocMarkers?: boolean;
};

function PdfProductTreeNodeRenderer({ item, depth, showTocMarkers }: Readonly<PdfProductTreeNodeRendererProps>) {
  const heading = item.title ?? item.tittel ?? item.description ?? "";
  const titleId = item.idName;
  const shouldRenderTocMarker = Boolean(item.pdfTocMarker);
  const shouldRenderSectionContent = !item.type || item.type === "seksjon";
  const shouldStartNewPage = item.type === "seksjon" && item.levelKapittel === 1;
  const headingLevel = ROOT_NODE_HEADING_LEVEL + depth;

  return (
    <div
      className="pdf-product-tree-node"
      data-level={item.levelKapittel}
      data-type={item.type}
      style={{ pageBreakBefore: shouldStartNewPage ? "always" : undefined }}
    >
      {shouldRenderTocMarker && item.prefix && (
        <div style={{ marginBottom: "10px", fontSize: "30px", fontWeight: "bold" }}>{item.prefix}</div>
      )}
      {heading && (
        <PdfHeading
          level={headingLevel}
          id={titleId}
        >
          {titleId && shouldRenderTocMarker && (
            <span className={showTocMarkers ? "pdf-toc-marker--probe" : "pdf-toc-marker"}>{item.pdfTocMarker}</span>
          )}
          {heading}
        </PdfHeading>
      )}

      {renderItemContent(item, headingLevel)}

      {shouldRenderSectionContent && item.intro && <p>{item.intro}</p>}
      {shouldRenderSectionContent && item.text && <TextBlock html={item.text} parentHeadingLevel={headingLevel} />}
      {shouldRenderSectionContent && !item.text && item.tekst && (
        <TextBlock html={item.tekst} parentHeadingLevel={headingLevel} />
      )}

      {item.children?.length ? (
        <div>
          {item.children.map((child, index) => (
            <PdfProductTreeNodeRenderer
              key={child.idName ?? child.id ?? `${item.idName ?? item.id ?? "child"}-${index}`}
              item={child}
              depth={depth + 1}
              showTocMarkers={showTocMarkers}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}

export type PdfProductTreeProps = {
  list?: PdfProductTreeNode[] | null;
  showMargin?: boolean;
  showTocMarkers?: boolean;
};

export default function PdfProductTree(props: Readonly<PdfProductTreeProps>) {
  const { list: listFromProps } = props;
  const { showMargin, showTocMarkers } = props;

  const list = listFromProps ?? [];

  return (
    <div style={{ marginBottom: showMargin ? "40px" : undefined }}>
      {list.map((item, index) => (
        <PdfProductTreeNodeRenderer
          key={item.idName ?? item.id ?? `${index}`}
          item={item}
          depth={0}
          showTocMarkers={showTocMarkers}
        />
      ))}
    </div>
  );
}
